import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Grid, Stack, Typography, Chip } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../api';
import Iconify from '../iconify/Iconify';
import DashboardFilters from './widgets/DashboardFilters';
import SummaryCards from './widgets/SummaryCards';
import TrendsChart from './widgets/TrendsChart';
import StatusDonut from './widgets/StatusDonut';
import ToPayBarChart from './widgets/ToPayBarChart';
import EmployeePerformanceTable from './widgets/EmployeePerformanceTable';
import RecentActivityFeed from './widgets/RecentActivityFeed';
import BillingSummaryPanel from './widgets/BillingSummaryPanel';

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const monthStart = new Date(today);
  monthStart.setDate(1);
  return {
    from: formatDateInput(monthStart),
    to: formatDateInput(today),
  };
};

const DEFAULT_FILTERS = {
  ...getDefaultDateRange(),
  employeeId: '',
  bucket: 'day',
  search: '',
  limit: 20,
};

const DEFAULT_PAGE_INFO = {
  total: 0,
  total_pages: 1,
  per_page: 10,
  current_page: 1,
};

const createWidgetState = (initialData) => ({
  data: initialData,
  loading: false,
  error: null,
});

const buildPayload = (filters, extra = {}) => ({
  query: {
    from: filters.from,
    to: filters.to,
    employeeId: filters.employeeId || '',
    bucket: filters.bucket || 'day',
    search: filters.search || '',
    limit: filters.limit || 20,
  },
  page: extra.page || 1,
  per_page: extra.per_page || 10,
});

const normalizeSummaryResponse = (res) => ({
  summary: res?.summary || {},
  billing: res?.billing || {},
});
const normalizeTrends = (res) => (Array.isArray(res?.series) ? res.series : []);
const normalizeStatuses = (res) => (Array.isArray(res?.statuses) ? res.statuses : []);
const normalizeTopay = (res) => ({
  totals: {
    pending: Number(res?.totalPendingAmount || res?.pending || 0),
    collected: Number(res?.totalCollectedAmount || res?.collected || 0),
    submitted: Number(res?.totalSubmittedAmount || res?.submitted || 0),
  },
  employees: Array.isArray(res?.employees) ? res.employees : [],
});
const normalizeEmployeePerformance = (res) => ({
  rows: Array.isArray(res?.rows) ? res.rows : [],
  pageInfo: { ...DEFAULT_PAGE_INFO, ...(res?.pageInfo || {}) },
});
const normalizeRecent = (res) => (Array.isArray(res?.activities) ? res.activities : []);

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

export default function AdminDashboard() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const debouncedFilters = useDebouncedValue(filters, 300);

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const [employeePageInfo, setEmployeePageInfo] = useState(DEFAULT_PAGE_INFO);

  const [summaryState, setSummaryState] = useState(createWidgetState({ summary: {}, billing: {} }));
  const [trendsState, setTrendsState] = useState(createWidgetState([]));
  const [statusState, setStatusState] = useState(createWidgetState([]));
  const [topayState, setTopayState] = useState(createWidgetState({ totals: {}, employees: [] }));
  const [employeePerfState, setEmployeePerfState] = useState(
    createWidgetState({ rows: [], pageInfo: DEFAULT_PAGE_INFO })
  );
  const [recentState, setRecentState] = useState(createWidgetState([]));

  const topayPendingByEmployee = useMemo(
    () =>
      (topayState.data?.employees || []).map((row) => ({
        employeeName: row.employeeName || 'NA',
        pending: Number(row.pendingSubmissionAmount || row.pending || 0),
      })),
    [topayState.data]
  );

  const fetchEmployeeOptions = async () => {
    try {
      const response = await api.get('/api/v1/employees/filter_options');
      if (response?.data?.success) {
        setEmployeeOptions(response.data.employees || []);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to load employee filters.');
    }
  };

  const fetchSummary = async (activeFilters) => {
    setSummaryState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await api.post('/api/v1/dashboard/summary', buildPayload(activeFilters));
      setSummaryState({ data: normalizeSummaryResponse(response?.data), loading: false, error: null });
    } catch (error) {
      setSummaryState((prev) => ({ ...prev, loading: false, error: error?.response?.data?.message || 'Error' }));
    }
  };

  const fetchTrends = async (activeFilters) => {
    setTrendsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await api.post('/api/v1/dashboard/trends', buildPayload(activeFilters));
      setTrendsState({ data: normalizeTrends(response?.data), loading: false, error: null });
    } catch (error) {
      setTrendsState((prev) => ({ ...prev, loading: false, error: error?.response?.data?.message || 'Error' }));
    }
  };

  const fetchStatusBreakdown = async (activeFilters) => {
    setStatusState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await api.post('/api/v1/dashboard/status_breakdown', buildPayload(activeFilters));
      setStatusState({ data: normalizeStatuses(response?.data), loading: false, error: null });
    } catch (error) {
      setStatusState((prev) => ({ ...prev, loading: false, error: error?.response?.data?.message || 'Error' }));
    }
  };

  const fetchTopaySummary = async (activeFilters) => {
    setTopayState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await api.post('/api/v1/dashboard/topay_summary', buildPayload(activeFilters));
      setTopayState({ data: normalizeTopay(response?.data), loading: false, error: null });
    } catch (error) {
      setTopayState((prev) => ({ ...prev, loading: false, error: error?.response?.data?.message || 'Error' }));
    }
  };

  const fetchEmployeePerformance = async (activeFilters, page = 1, perPage = 10) => {
    setEmployeePerfState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await api.post(
        '/api/v1/dashboard/employee_performance',
        buildPayload(activeFilters, { page, per_page: perPage })
      );
      const normalized = normalizeEmployeePerformance(response?.data);
      setEmployeePageInfo(normalized.pageInfo);
      setEmployeePerfState({ data: normalized, loading: false, error: null });
    } catch (error) {
      setEmployeePerfState((prev) => ({ ...prev, loading: false, error: error?.response?.data?.message || 'Error' }));
    }
  };

  const fetchRecentActivity = async (activeFilters) => {
    setRecentState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await api.post('/api/v1/dashboard/recent_activity', buildPayload(activeFilters));
      setRecentState({ data: normalizeRecent(response?.data), loading: false, error: null });
    } catch (error) {
      setRecentState((prev) => ({ ...prev, loading: false, error: error?.response?.data?.message || 'Error' }));
    }
  };

  const fetchAllWidgets = async ({
    withLoading = true,
    page = 1,
    perPage = 10,
    activeFilters = debouncedFilters,
  } = {}) => {
    if (withLoading) setRefreshing(true);

    await Promise.all([
      fetchSummary(activeFilters),
      fetchTrends(activeFilters),
      fetchStatusBreakdown(activeFilters),
      fetchTopaySummary(activeFilters),
      fetchEmployeePerformance(activeFilters, page, perPage),
      fetchRecentActivity(activeFilters),
    ]);

    if (withLoading) setRefreshing(false);
  };

  useEffect(() => {
    fetchEmployeeOptions();
  }, []);

  useEffect(() => {
    setEmployeePageInfo((prev) => ({ ...prev, current_page: 1 }));
    fetchAllWidgets({
      withLoading: false,
      page: 1,
      perPage: DEFAULT_PAGE_INFO.per_page,
      activeFilters: debouncedFilters,
    });
  }, [debouncedFilters]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleRefresh = () => {
    fetchAllWidgets({
      withLoading: true,
      page: employeePageInfo.current_page,
      perPage: employeePageInfo.per_page,
      activeFilters: filters,
    });
  };

  const handleEmployeePageChange = async (event, nextPage) => {
    await fetchEmployeePerformance(debouncedFilters, nextPage, employeePageInfo.per_page || 10);
  };

  return (
    <>
      <Helmet>
        <title>City Courier | Dashboard</title>
      </Helmet>

      <Box
        sx={{
          minHeight: '100%',
          py: { xs: 2, md: 3 },
          background:
            'radial-gradient(circle at 0% 0%, rgba(0,120,212,0.18), transparent 30%), radial-gradient(circle at 100% 0%, rgba(0,166,118,0.16), transparent 28%), linear-gradient(180deg, #f2f8ff 0%, #ffffff 42%)',
        }}
      >
        <Container maxWidth={false}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
            sx={{
              mb: 2.5,
              p: 2.2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'rgba(255,255,255,0.7)',
              background: 'linear-gradient(140deg, rgba(255,255,255,0.86), rgba(255,255,255,0.6))',
              backdropFilter: 'blur(6px)',
              boxShadow: '0 10px 28px rgba(16, 24, 40, 0.08)',
            }}
          >
            <Stack spacing={0.6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 0.1,
                  fontSize: { xs: '1.65rem', md: '2.05rem' },
                  lineHeight: { xs: 1.2, md: 1.15 },
                }}
              >
                Dashboard
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.82rem', md: '0.9rem' }, maxWidth: 560 }}
              >
                Performance, delivery flow, bills and to-pay visibility in one place.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                icon={<Iconify icon="solar:check-read-bold" />}
                color="success"
                variant="filled"
                label={`Delivered ${Number(summaryState.data?.summary?.delivered || 0).toLocaleString()}`}
                sx={{
                  bgcolor: 'success.main',
                  color: 'common.white',
                  border: '1px solid rgba(255,255,255,0.35)',
                  '& .MuiChip-icon': { color: 'common.white' },
                }}
              />
              <Chip
                icon={<Iconify icon="mdi:cash-clock" />}
                color="warning"
                variant="filled"
                label={`Pending ₹${Number(summaryState.data?.summary?.toPayPending || 0).toLocaleString()}`}
                sx={{
                  bgcolor: 'warning.dark',
                  color: 'common.white',
                  border: '1px solid rgba(255,255,255,0.35)',
                  '& .MuiChip-icon': { color: 'common.white' },
                }}
              />
            </Stack>
          </Stack>

          <DashboardFilters
            filters={filters}
            employeeOptions={employeeOptions}
            onChange={handleFilterChange}
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />

          <SummaryCards
            loading={summaryState.loading}
            error={summaryState.error}
            data={summaryState.data?.summary}
            onRetry={() => fetchSummary(debouncedFilters)}
          />

          <Grid container spacing={2.5} sx={{ mb: 2.8 }}>
            <Grid item xs={12} lg={8}>
              <TrendsChart
                data={trendsState.data}
                loading={trendsState.loading}
                error={trendsState.error}
                onRetry={() => fetchTrends(debouncedFilters)}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <ToPayBarChart
                data={topayPendingByEmployee}
                loading={topayState.loading}
                error={topayState.error}
                onRetry={() => fetchTopaySummary(debouncedFilters)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mb: 2.8 }}>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <StatusDonut
                data={statusState.data}
                loading={statusState.loading}
                error={statusState.error}
                onRetry={() => fetchStatusBreakdown(debouncedFilters)}
                recentActivities={recentState.data}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <BillingSummaryPanel
                data={summaryState.data?.billing}
                loading={summaryState.loading}
                error={summaryState.error}
                onRetry={() => fetchSummary(debouncedFilters)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={8}>
              <EmployeePerformanceTable
                rows={employeePerfState.data?.rows || []}
                pageInfo={employeePageInfo}
                loading={employeePerfState.loading}
                error={employeePerfState.error}
                onRetry={() =>
                  fetchEmployeePerformance(
                    debouncedFilters,
                    employeePageInfo.current_page || 1,
                    employeePageInfo.per_page || 10
                  )
                }
                onPageChange={handleEmployeePageChange}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <RecentActivityFeed
                rows={recentState.data}
                loading={recentState.loading}
                error={recentState.error}
                onRetry={() => fetchRecentActivity(debouncedFilters)}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
